import { Add } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { Observation } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Observation';
import {
  ObservationWrapper,
  WrappedObservation,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { useObservations, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { TableHeadCustom } from 'src/components/table';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTable from 'src/hooks/useTable';
import { TABLE_HEAD_OBSERVATION } from 'src/careflow/common/table-head';
import { translateTableHead } from 'src/careflow/helpers/common';
import { v4 as uuidv4 } from 'uuid';
import Row from './Row';
import { ObservationForm } from './ObservationForm';

type Props = {
  patient: WrappedPatient | null | undefined;
  customQuery?: any;
  showCreate?: boolean;
  showEmpty?: boolean;
  permissions: {
    isAllowedAdd: boolean;
    isAllowedEdit: boolean;
  };
  handlerAction?: Function;
};

export default function ObservationDetails({
  patient,
  customQuery,
  showCreate = true,
  showEmpty = false,
  permissions,
  handlerAction
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<any>({});

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});

  const [valueSetCategory] = useValueSet('ph-category-observation-filter', { map: ValueSetWrapper });
  const { i18n } = useLocales();

  const observationsFilter = useMemo(() => {
    if (customQuery) {
      return customQuery;
    }

    const cats = valueSetCategory?.asList()?.map((item) => item?.code) ?? [];
    const types = [];
    const filters = [...cats, ...types];

    const combinedFilter = filters && filters.length > 0 ? filters?.join(',') : '';

    const filter = {
      ...(patient?.id && { patient: patient?.id }),
      ...(combinedFilter && { category: combinedFilter })
    };

    return filter;
  }, [patient?.id, valueSetCategory, customQuery]);

  const canFetch = useMemo(() => {
    if(showEmpty) {
      return false
    }
    if (customQuery) {
      return true;
    }
    if (
      patient?.id &&
      observationsFilter &&
      observationsFilter?.patient //&& observationsFilter?.category
    ) {
      return true;
    }
    return false;
  }, [patient?.id, observationsFilter, customQuery, showEmpty]);

  const [observations, { create: createObservation, update: updateObservation }] = useObservations({
    filter: observationsFilter,
    map: ObservationWrapper,
    autofetch: !!patient?.id && canFetch,
  });

  const saveObservation = async (observation: Observation) => {
    try {
      const payload = {
        ...observation,
        ...(customQuery?.['based-on'] && { basedOn: [{reference: `CarePlan/${customQuery['based-on']}`}]})
      }
      if (isEmpty(editRow)) {
        if (observation) {
          const result = await createObservation(payload);
          enqueueSnackbar('Observation was created.');
          handleClose();
        }
      } else {
        const observationEdit = editRow as Observation;
        observationEdit.status = 'amended';
        const createDerivedObservation = payload as Observation;
        if (createDerivedObservation) {
          const resultCreate = await createObservation(createDerivedObservation);
          const resultUpdate = await updateObservation(observationEdit);
          enqueueSnackbar('Observation was updated.');
          handleClose();
        }
      }
      handlerAction?.();
    } catch (error) {
      enqueueSnackbar('Observation was not created.', { variant: 'error' });
      handleClose();
    }
  };

  const handleClose = () => {
    setEditRow({});
    setOpen(false);
  };

  const handleEdit = (data: any) => {
    setEditRow(data);
    setOpen(true);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <TableContainer>
            <Stack sx={{ alignContent: 'end', flexWrap: 'wrap', m: 1 }}>
              {permissions?.isAllowedAdd && showCreate && (
                <Button
                  size="small"
                  variant="contained"
                  sx={{ height: '36px' }}
                  startIcon={<Add />}
                  style={{ right: '25px' }}
                  onClick={() => setOpen(true)}
                >
                  {i18n('patients.details.observations.button', 'crs')}
                </Button>
              )}
            </Stack>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHeadCustom headLabel={translateTableHead(TABLE_HEAD_OBSERVATION, i18n, 'crs')} />
              <TableBody>
                {!isEmpty(observations) ? (
                  observations
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row: WrappedObservation) => (
                      <Row 
                        key={uuidv4()} 
                        row={row} 
                        isAllowedEdit={permissions.isAllowedEdit} 
                        handleEdit={handleEdit} 
                      />
                    ))
                ) : (
                  <TableCell colSpan={TABLE_HEAD_OBSERVATION?.length}>
                    <Typography variant="body2" align="center">
                      No rows
                    </Typography>
                  </TableCell>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={observations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>
      <ObservationForm
        patient={patient}
        carePlanId={customQuery?.['based-on'] ?? null}
        open={open}
        observation={editRow}
        onClose={handleClose}
        handleObservation={saveObservation}
      />
    </>
  );
}
